import { useState } from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import CenteredModal from "@/src/common/centered-modal/CenteredModal"
import { Iconify } from "@/src/common/iconify"
import { useInternalStore } from "@/src/store/slices/internal.slice"
import { deleteJobLeads } from "@/src/services/internal"

// ----------------------------------------------------------------------

export interface DeleteLeadsModalProps {
    open: boolean
    onClose: () => void
    jobName: string
    jobId?: string
    /** Workspace ID for which leads will be deleted (required for delete API) */
    workspaceId?: string
    onSuccess?: () => void
}

const PLACEHOLDER = `https://example.com/candidate/123
https://example.com/candidate/456`

// ----------------------------------------------------------------------

export function DeleteLeadsModal({
    open,
    onClose,
    jobId,
    workspaceId: workspaceIdProp,
    onSuccess,
}: DeleteLeadsModalProps) {
    const [candidateLinks, setCandidateLinks] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { selectedJob } = useInternalStore()

    const effectiveJobId = jobId ?? selectedJob?.jobId
    const effectiveWorkspaceId = workspaceIdProp ?? selectedJob?.workspaceId

    const handleClose = () => {
        setCandidateLinks("")
        setError(null)
        onClose()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        const links = candidateLinks
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        if (links.length === 0) return
        if (!effectiveWorkspaceId) {
            setError("Workspace not selected.")
            return
        }
        setSubmitting(true)
        try {
            await deleteJobLeads(effectiveWorkspaceId, { urls: links })
            onSuccess?.()
            handleClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete leads")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <CenteredModal open={open} onClose={handleClose} width={520}>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    "& > *": { flexShrink: 0 },
                }}
            >
                {/* Header with red title and close */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} color="error">
                        Delete Leads
                    </Typography>
                    <IconButton onClick={handleClose} size="small" aria-label="Close">
                        <Iconify icon="eva:close-fill" width={24} />
                    </IconButton>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Paste candidate links for <strong>{selectedJob?.jobName}</strong> below (one per line). Matching
                    leads will be permanently deleted.
                </Typography>

                {error && (
                    <Typography variant="body2" color="error">
                        {error}
                    </Typography>
                )}

                <TextField
                    label="Candidate Links"
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={candidateLinks}
                    onChange={(e) => setCandidateLinks(e.target.value)}
                    placeholder={PLACEHOLDER}
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { alignItems: "flex-start" } }}
                />

                <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2 }}>
                    <Button variant="outlined" color="inherit" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="error" disabled={submitting}>
                        Delete Leads
                    </Button>
                </Stack>
            </Box>
        </CenteredModal>
    )
}
